import { Button } from '@mui/material';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import useDebounce from '../../../../hooks/useDebounce';
import { fetchAssociatedTestCases } from '../../../../modules/associations';
import { getTestSuites } from '../../../../modules/testsuites';
import Modal from '../../../elements/Modal';
import SearchBar from '../../../elements/SearchBar';
import TableComponent from '../../../elements/TableComponent';

const headers = [
    { key: 'key', label: 'Key', sortable: true },
    { key: 'summary', label: 'Summary', sortable: true },
    { key: 'suite', label: 'Test Suite', sortable: true },
    { key: 'execution_order_no', label: 'Order', sortable: false, width: '60px' },
];

const TestCaseTable = ({
    entityId,
    projectId,
    addNotification,
    selectedTestCases,
    setSelectedTestCases,
    show,
    handleCloseButton,
    handleAddTestCases,
    setFetchedAssociations,
}) => {

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedTestSuite, setSelectedTestSuite] = useState('all');
    const [testSuites, setTestSuites] = useState([]);
    const [order, setOrder] = useState('asc');
    const [availableTestSuiteIds, setAvailableTestSuiteIds] = useState(new Set());

    const debouncedSearch = useDebounce(searchValue, 400);

    useEffect(() => {
        getTestSuites(projectId, 0, 'all', '', 'active')
            .then(res => setTestSuites(res.data))
            .catch(() => { });
    }, [projectId]);

    const fetchLinked = useCallback(async (search, suite) => {
        setLoading(true);
        try {
            const suiteFilter = suite === 'all' ? [] : [suite];

            const res = await fetchAssociatedTestCases(
                entityId,
                projectId,
                search,
                suiteFilter,
                'Approved',
                null,
                1,
                'all',
                true,
            );
             
            const list = res?.associations || [];
            setData(list);
            setFetchedAssociations(list);

             if (suite === 'all') {
            const suiteIds = new Set();
            list.forEach(association => {
                const testSuites = association?.testcase?.test_suites || [];
                testSuites.forEach(suiteId => suiteIds.add(suiteId));
            });
            setAvailableTestSuiteIds(suiteIds);
        }

        } catch (err) {
            addNotification('danger', 'Failed to fetch test cases.');
        } finally {
            setLoading(false);
        }
    }, [entityId, projectId, addNotification, setFetchedAssociations]);
    const filteredTestSuites = useMemo(() => {
        return testSuites.filter(suite => availableTestSuiteIds.has(suite.id));
    }, [testSuites, availableTestSuiteIds]);
    
    useEffect(() => {
        if (!show) return;
        fetchLinked(debouncedSearch, selectedTestSuite);
    }, [show, debouncedSearch, selectedTestSuite, fetchLinked]);

  const tableData = useMemo(() => {
    const mapped = data.map(a => {
        const testCaseSuiteIds = a.testcase?.test_suites || [];
        const suiteNames = testCaseSuiteIds
            .map(suiteId => {
                const suite = testSuites.find(s => s.id === suiteId);
                return suite?.name || '';
            })
            .filter(Boolean)
            .join(', ');

        return {
            id: a.id,
            key: a.testcase?.key || '',
            summary: a.testcase?.summary || '',
            suite: suiteNames || 'No Suite',
            execution_order_no:
                selectedTestCases.indexOf(a.id) >= 0
                    ? `#${selectedTestCases.indexOf(a.id) + 1}`
                    : '',
            assignedTo: a?.assigned_to ?? a?.assignedTo ?? null,
        };
    });

    return mapped.sort((a, b) => {
        if (a.suite < b.suite) return -1;
        if (a.suite > b.suite) return 1;
        return 0;
    });
}, [data, selectedTestCases, testSuites]);

    return (
        <Modal
            modalTitle="Select Test Case(s)"
            open={show}
            handleClose={handleCloseButton}
            maxWidth="md"
            action={() => (
                <>
                    <Button variant="outlined" onClick={handleCloseButton}>Close</Button>
                    <Button
                        variant="contained"
                        disabled={selectedTestCases.length === 0}
                        onClick={handleAddTestCases}
                    >
                        Add ({selectedTestCases.length})
                    </Button>
                </>
            )}
        >
            <Row className="mb-3">
                <Col md={4}>
                    <SearchBar
                        value={searchValue}
                        onChange={e => setSearchValue(e.target.value)}
                        clearSearch={() => setSearchValue('')}
                    />
                </Col>

                <Col sm={3}>
                    <Form.Select
                        value={selectedTestSuite}
                        onChange={e => setSelectedTestSuite(e.target.value)}
                    >
                        <option value="all">All Suites</option>
                        {filteredTestSuites.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            <TableComponent
                data={tableData}
                headerTabs={headers}
                isLoading={loading}
                order={order}
                setOrder={setOrder}
                striped
                showCheckbox
                selected={selectedTestCases}
                setSelected={setSelectedTestCases}
                tablekey="test cases"
                showPages={false}
                page={1}
                perPage={tableData.length}
                totalCount={tableData.length}
            />
        </Modal>
    );
};

export default TestCaseTable;
